import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated, Easing, Modal, Platform, Pressable, Text, View,
  useWindowDimensions,
} from 'react-native';
import { Image } from 'expo-image';
import { Check, Clock, Flame, Gift, Trophy, X } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getAssetUrl, ASSET_PATHS, IDLE_COIN_SOURCE } from '../../../../constants/assets';
import { useEconomyStore } from '../../../shared/stores/economyStore';
import { getRealActiveStreak } from '../../helpers/dailyClaimStreakDisplay.helper';
import { useCountdownToDate } from '../../../challenges/hooks/useCountdownToDate';
import { DAILY_CLAIM_LADDER, MAX_DAILY_CLAIM_COINS } from '../../constants/economy.constants';
import {
  getDailyRewardModalStyles,
  GOLD, MINT, PURPLE_LIGHT,
} from './DailyRewardModal.styles';

type Phase = 'idle' | 'collecting' | 'claimed';

const PARTICLE_COUNT = 14;
const CONFETTI_COUNT = 16;
const CONFETTI_COLORS = [GOLD, '#A78BFA', MINT, '#F87171', '#FDE68A', PURPLE_LIGHT];
const USE_NATIVE = Platform.OS !== 'web';

function useCounterTo(target: number, duration = 900): number {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    if (fromRef.current === target) { setVal(target); return; }
    const from = fromRef.current;
    const start = Date.now();
    let raf: ReturnType<typeof requestAnimationFrame>;
    const tick = () => {
      const t = Math.min(1, (Date.now() - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setVal(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

export interface DailyRewardModalProps {
  isVisible: boolean;
  onDismiss: () => void;
  onClaim: () => void;
  testID?: string;
}

export const DailyRewardModal: React.FC<DailyRewardModalProps> = ({
  isVisible, onDismiss, onClaim, testID,
}) => {
  const styles = useMemo(() => getDailyRewardModalStyles(), []);
  const { width: screenW, height: screenH } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  // Height of the existing app header we leave uncovered so it shows through
  const headerClearance = insets.top + 62;

  const dailyClaimStreak = useEconomyStore((s) => s.dailyClaimStreak);
  const lastDailyClaimAt = useEconomyStore((s) => s.lastDailyClaimAt);
  const nextClaimAt = useEconomyStore((s) => s.nextClaimAt);
  const isClaiming = useEconomyStore((s) => s.isClaiming);
  const storeCoins = useEconomyStore((s) => s.coins);
  const { segments } = useCountdownToDate(nextClaimAt);

  const activeStreak = getRealActiveStreak(dailyClaimStreak, lastDailyClaimAt);
  const currentDay = ((activeStreak - 1) % 7) + 1;
  const reward = DAILY_CLAIM_LADDER[currentDay - 1];

  const [phase, setPhase] = useState<Phase>('idle');
  const [coinTarget, setCoinTarget] = useState(storeCoins);
  const displayedCoins = useCounterTo(coinTarget);

  useEffect(() => {
    if (isVisible) {
      const alreadyClaimed = nextClaimAt !== null;
      setPhase(alreadyClaimed ? 'claimed' : 'idle');
      setCoinTarget(storeCoins);
      if (alreadyClaimed) {
        checkScale.setValue(1);
        progressFill.setValue(currentDay / 6);
        pileScale.setValue(1);
        pileOp.setValue(1);
      } else {
        checkScale.setValue(0);
        pileScale.setValue(1);
        pileOp.setValue(1);
      }
    }
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Authoritative balance from economy store (API) — avoids optimistic +reward drift vs server.
  useEffect(() => {
    if (!isVisible) return;
    setCoinTarget(storeCoins);
  }, [isVisible, storeCoins]);

  // ── Continuous idle animations ─────────────────────────────────
  const sheetY = useRef(new Animated.Value(80)).current;
  const coinFloat = useRef(new Animated.Value(0)).current;
  const shimmerX = useRef(new Animated.Value(-100)).current;
  const sparkOp = useRef(Array.from({ length: 4 }, () => new Animated.Value(0))).current;
  const progressFill = useRef(new Animated.Value(0)).current;
  const todayPulse = useRef(new Animated.Value(1)).current;

  // ── Collect animations ─────────────────────────────────────────
  const pileScale = useRef(new Animated.Value(1)).current;
  const pileOp = useRef(new Animated.Value(1)).current;
  const checkScale = useRef(new Animated.Value(0)).current;

  // ── Particles ──────────────────────────────────────────────────
  const particles = useRef(
    Array.from({ length: PARTICLE_COUNT }, () => ({
      x: new Animated.Value(0), y: new Animated.Value(0),
      op: new Animated.Value(0), sc: new Animated.Value(0.4),
    }))
  ).current;
  const confetti = useRef(
    Array.from({ length: CONFETTI_COUNT }, () => ({
      x: new Animated.Value(0), y: new Animated.Value(-20),
      rot: new Animated.Value(0), op: new Animated.Value(0),
    }))
  ).current;

  const idleRef = useRef<Animated.CompositeAnimation[]>([]);
  const ctaRef = useRef<View>(null);

  useEffect(() => {
    idleRef.current.forEach((a) => a.stop());
    idleRef.current = [];
    if (!isVisible) { sheetY.setValue(80); return; }

    Animated.spring(sheetY, { toValue: 0, damping: 22, stiffness: 200, useNativeDriver: USE_NATIVE }).start();

    const filled = nextClaimAt ? currentDay : currentDay - 1;
    progressFill.setValue(0);
    Animated.timing(progressFill, { toValue: filled / 6, duration: 800, delay: 400, useNativeDriver: false }).start();

    const loops: Animated.CompositeAnimation[] = [
      Animated.loop(Animated.sequence([
        Animated.timing(coinFloat, { toValue: -8, duration: 1600, easing: Easing.inOut(Easing.sin), useNativeDriver: USE_NATIVE }),
        Animated.timing(coinFloat, { toValue: 0, duration: 1600, easing: Easing.inOut(Easing.sin), useNativeDriver: USE_NATIVE }),
      ])),
      Animated.loop(Animated.sequence([
        Animated.timing(shimmerX, { toValue: 420, duration: 1400, useNativeDriver: USE_NATIVE }),
        Animated.delay(900),
        Animated.timing(shimmerX, { toValue: -100, duration: 0, useNativeDriver: USE_NATIVE }),
      ])),
      Animated.loop(Animated.sequence([
        Animated.timing(todayPulse, { toValue: 1.12, duration: 900, easing: Easing.inOut(Easing.sin), useNativeDriver: USE_NATIVE }),
        Animated.timing(todayPulse, { toValue: 1, duration: 900, easing: Easing.inOut(Easing.sin), useNativeDriver: USE_NATIVE }),
      ])),
    ];
    loops.forEach((a) => { a.start(); idleRef.current.push(a); });

    [0, 400, 900, 1300].forEach((delay, i) => {
      const sp = Animated.loop(Animated.sequence([
        Animated.delay(delay),
        Animated.timing(sparkOp[i], { toValue: 1, duration: 500, useNativeDriver: USE_NATIVE }),
        Animated.timing(sparkOp[i], { toValue: 0, duration: 500, useNativeDriver: USE_NATIVE }),
        Animated.delay(800),
      ]));
      sp.start();
      idleRef.current.push(sp);
    });

    return () => { idleRef.current.forEach((a) => a.stop()); };
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isVisible) { pileScale.setValue(1); pileOp.setValue(1); checkScale.setValue(0); }
  }, [isVisible, pileScale, pileOp, checkScale]);

  const runCollect = useCallback(() => {
    if (phase !== 'idle' || isClaiming) return;
    setPhase('collecting');
    onClaim();

    // Target: coin display in the existing app header (top-left area)
    const headerCoinX = 90;
    const headerCoinY = insets.top + 30;

    const doAnimate = (ctaCX: number, ctaCY: number) => {
      const dx = headerCoinX - ctaCX;
      const dy = headerCoinY - ctaCY;

      const burstAnims = particles.map((p, i) => {
        const spread = (Math.random() - 0.5) * 200;
        const arc = -100 - Math.random() * 80;
        const dur = 680 + Math.random() * 220;
        p.x.setValue(0); p.y.setValue(0); p.op.setValue(0); p.sc.setValue(0.4);
        return Animated.sequence([
          Animated.delay(i * 26),
          Animated.parallel([
            Animated.sequence([
              Animated.timing(p.x, { toValue: spread, duration: dur * 0.38, useNativeDriver: USE_NATIVE }),
              Animated.timing(p.x, { toValue: dx, duration: dur * 0.62, useNativeDriver: USE_NATIVE }),
            ]),
            Animated.sequence([
              Animated.timing(p.y, { toValue: arc, duration: dur * 0.38, useNativeDriver: USE_NATIVE }),
              Animated.timing(p.y, { toValue: dy, duration: dur * 0.62, useNativeDriver: USE_NATIVE }),
            ]),
            Animated.sequence([
              Animated.timing(p.op, { toValue: 1, duration: 120, useNativeDriver: USE_NATIVE }),
              Animated.delay(dur * 0.68),
              Animated.timing(p.op, { toValue: 0, duration: dur * 0.32, useNativeDriver: USE_NATIVE }),
            ]),
            Animated.timing(p.sc, { toValue: 0.9 + Math.random() * 0.3, duration: dur * 0.4, useNativeDriver: USE_NATIVE }),
          ]),
        ]);
      });

      const confettiAnims = confetti.map((c) => {
        const xStart = 20 + Math.random() * (screenW - 40);
        const xDrift = (Math.random() - 0.5) * 120;
        c.x.setValue(xStart); c.y.setValue(headerClearance - 20); c.op.setValue(0); c.rot.setValue(0);
        return Animated.sequence([
          Animated.delay(Math.random() * 180),
          Animated.parallel([
            Animated.timing(c.y, { toValue: headerClearance + screenH * 0.55, duration: 1600 + Math.random() * 400, useNativeDriver: USE_NATIVE }),
            Animated.timing(c.x, { toValue: xStart + xDrift, duration: 1800, useNativeDriver: USE_NATIVE }),
            Animated.timing(c.rot, { toValue: 720, duration: 2000, useNativeDriver: USE_NATIVE }),
            Animated.sequence([
              Animated.timing(c.op, { toValue: 1, duration: 200, useNativeDriver: USE_NATIVE }),
              Animated.timing(c.op, { toValue: 0, duration: 600, delay: 1000, useNativeDriver: USE_NATIVE }),
            ]),
          ]),
        ]);
      });

      Animated.parallel([...burstAnims, ...confettiAnims]).start();

      Animated.sequence([
        Animated.timing(pileScale, { toValue: 1.2, duration: 160, useNativeDriver: USE_NATIVE }),
        Animated.parallel([
          Animated.timing(pileScale, { toValue: 0.1, duration: 380, useNativeDriver: USE_NATIVE }),
          Animated.timing(pileOp, { toValue: 0, duration: 380, useNativeDriver: USE_NATIVE }),
        ]),
      ]).start(() => {
        setPhase('claimed');
        Animated.spring(checkScale, { toValue: 1, damping: 12, stiffness: 220, useNativeDriver: USE_NATIVE }).start();
        Animated.timing(progressFill, { toValue: currentDay / 6, duration: 600, useNativeDriver: false }).start();
      });

    };

    if (ctaRef.current) {
      ctaRef.current.measure((_x, _y, w, h, pageX, pageY) => {
        doAnimate(pageX + w / 2, pageY + h / 2);
      });
    } else {
      doAnimate(screenW / 2, screenH - 65);
    }
  }, [phase, isClaiming, onClaim, particles, confetti, reward, currentDay,
      pileScale, pileOp, checkScale, progressFill, insets.top, headerClearance,
      screenW, screenH]);

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
      statusBarTranslucent
      presentationStyle="overFullScreen"
      testID={testID ?? 'daily-reward-modal'}
    >
      {/* Transparent gap at top — existing app header shows through untouched */}
      <View style={{ flex: 1 }}>
        <View style={{ height: headerClearance }} />

        {/* Scrim + sheet below the header */}
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(8,5,20,0.82)', justifyContent: 'flex-end' }}
          onPress={onDismiss}
        >
          {/* ── Bottom sheet ── */}
          <Animated.View
            style={[styles.sheet, { transform: [{ translateY: sheetY }] }]}
            onStartShouldSetResponder={() => true}
          >
            <View style={styles.handle} />

            <View style={styles.head}>
              <View>
                <Text style={styles.eyebrow}>DAILY REWARD</Text>
                <Text style={styles.titleText}>
                  {phase === 'claimed' ? 'Reward Claimed!' : `Day ${currentDay} of 7`}
                </Text>
                <Text style={styles.metaText}>
                  {phase === 'claimed' ? 'Keep the streak — come back tomorrow.' : 'Open the app daily to grow your streak.'}
                </Text>
              </View>
              <Pressable style={styles.closeBtn} onPress={onDismiss} accessibilityRole="button" accessibilityLabel="Close">
                <X size={18} color="#D1D5DB" />
              </Pressable>
            </View>

            {/* ── Hero ── */}
            <View style={styles.heroStage}>
              {phase !== 'claimed' ? (
                <>
                  {sparkOp.map((op, i) => (
                    <Animated.View
                      key={i}
                      style={[styles.sparkWrap, (styles as Record<string, unknown>)[`spark${i + 1}`] as object, { opacity: op }]}
                    >
                      <Text style={{ fontSize: i < 2 ? 18 : 13, color: GOLD }}>✦</Text>
                    </Animated.View>
                  ))}
                  <Animated.View style={[styles.coinPileWrap, {
                    transform: [{ translateY: coinFloat }, { scale: pileScale }],
                    opacity: pileOp,
                  }]}>
                    <View style={styles.groundShadow} />
                    <Image
                      source={{ uri: getAssetUrl(ASSET_PATHS.COINS.COIN_150) }}
                      style={styles.coinPileImg}
                      contentFit="contain"
                      alt="Coins"
                    />
                  </Animated.View>
                </>
              ) : (
                <Animated.View style={[styles.checkDisc, { transform: [{ scale: checkScale }] }]}>
                  <Check size={52} color="#062417" strokeWidth={3} />
                </Animated.View>
              )}
            </View>

            {phase !== 'claimed' ? (
              <>
                <View style={styles.amountRow}>
                  <Text style={styles.amountPlus}>+</Text>
                  <Text style={styles.amountNum}>{reward}</Text>
                  <Text style={styles.amountUnit}>COINS</Text>
                </View>
                <View style={styles.streakChip}>
                  <Flame size={14} color={GOLD} fill={GOLD} />
                  <Text style={styles.streakText}>
                    Day {currentDay} of 7 · Day 7 unlocks <Text style={styles.streakAccent}>{MAX_DAILY_CLAIM_COINS} coins</Text>
                  </Text>
                </View>
              </>
            ) : (
              <>
                <Text style={styles.claimedAmountText}>+{reward} coins</Text>
                <Text style={styles.claimedSubText}>Added to your wallet</Text>
              </>
            )}

            {/* ── Progress path ── */}
            <View style={styles.pathContainer}>
              <View style={styles.trackBg}>
                <Animated.View style={[styles.trackFill, {
                  width: progressFill.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
                }]}>
                  <LinearGradient colors={[MINT, GOLD]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={{ flex: 1 }} />
                </Animated.View>
              </View>
              <View style={styles.daysRow}>
                {DAILY_CLAIM_LADDER.map((amt, idx) => {
                  const day = idx + 1;
                  const isDone = day < currentDay || (phase === 'claimed' && day === currentDay);
                  const isToday = !isDone && day === currentDay;
                  const isFinale = !isDone && !isToday && day === 7;
                  return (
                    <View key={day} style={styles.dayCol}>
                      <Animated.View style={[
                        styles.dayNode,
                        isDone && styles.dayNodeDone,
                        isToday && styles.dayNodeToday,
                        isFinale && styles.dayNodeFinale,
                        isToday && { transform: [{ scale: todayPulse }] },
                      ]}>
                        {isDone
                          ? <Check size={14} color="#062417" strokeWidth={3.5} />
                          : isFinale
                          ? <Trophy size={16} color="#1A1330" strokeWidth={2} />
                          : <Text style={isToday ? styles.dayNodeNumToday : styles.dayNodeNum}>{day}</Text>}
                      </Animated.View>
                      <Text style={[styles.dayLabel, isDone && styles.dayLabelDone, isToday && styles.dayLabelToday, isFinale && styles.dayLabelFinale]}>
                        {isToday ? 'TODAY' : `Day ${day}`}
                      </Text>
                      <Text style={[styles.dayAmount, isToday && styles.dayAmountToday, isFinale && styles.dayAmountFinale]}>
                        {amt}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </View>

            {/* ── CTA ── */}
            <View style={styles.ctaZone}>
              {phase !== 'claimed' ? (
                <Pressable
                  ref={ctaRef}
                  collapsable={false}
                  style={({ pressed }) => [styles.ctaBtn, pressed && styles.ctaBtnPressed]}
                  onPress={runCollect}
                  disabled={phase === 'collecting'}
                  accessibilityRole="button"
                  accessibilityLabel={`Collect ${reward} coins`}
                  testID={testID ? `${testID}-collect` : 'daily-reward-collect'}
                >
                  <Animated.View
                    pointerEvents="none"
                    style={[styles.shimmerStrip, { transform: [{ translateX: shimmerX }] }]}
                  />
                  <Gift size={20} color="#1A202C" strokeWidth={2.4} />
                  <Text style={styles.ctaText}>
                    {phase === 'collecting' ? 'Collecting…' : `Collect ${reward} Coins`}
                  </Text>
                </Pressable>
              ) : (
                <View style={[styles.ctaBtn, styles.ctaBtnDisabled]}>
                  <Clock size={18} color="#6B7280" />
                  <Text style={[styles.ctaText, styles.ctaTextDisabled]}>
                    {segments ? `Next reward in ${segments.hours}h ${segments.minutes}m` : 'Come back tomorrow'}
                  </Text>
                </View>
              )}
              <View style={styles.footerStrip}>
                <Text style={styles.footerText}>
                  Wallet balance: <Text style={styles.footerBold}>{displayedCoins.toLocaleString()} coins</Text>
                </Text>
              </View>
            </View>
          </Animated.View>

        </Pressable>

        {/* ── Particle + confetti layer — full-screen so coins reach the header ── */}
        <View style={styles.particleLayer} pointerEvents="none">
          {particles.map((p, i) => (
            <Animated.View key={i} style={[styles.particle, {
              left: screenW / 2 - 13,
              top: screenH - 65,
              opacity: p.op,
              transform: [{ translateX: p.x }, { translateY: p.y }, { scale: p.sc }],
            }]}>
              <Image source={IDLE_COIN_SOURCE} style={{ width: 26, height: 26 }} alt="" contentFit="contain" />
            </Animated.View>
          ))}
          {confetti.map((c, i) => (
            <Animated.View key={i} style={[styles.confettiPiece, {
              width: i % 3 === 0 ? 8 : 7,
              height: i % 3 === 0 ? 8 : 11,
              borderRadius: i % 2 === 0 ? 4 : 2,
              backgroundColor: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              opacity: c.op,
              transform: [
                { translateX: c.x },
                { translateY: c.y },
                { rotate: c.rot.interpolate({ inputRange: [0, 720], outputRange: ['0deg', '720deg'] }) },
              ],
            }]} />
          ))}
        </View>
      </View>
    </Modal>
  );
};
